import { ReactNode, Children, useRef, useLayoutEffect, useState } from 'react';
import { Maybe } from '@ardoq/common-helpers';
import { Badge, StatusType } from '@ardoq/status-ui';
import { IconName } from '@ardoq/icons';
import { ValueContainerStyle } from './ValueContainer';

const MAX_DISTANCE_IN_PX_FROM_CHILD_TO_PARENT_CONTAINER_TOP = 60; // This should be enough to fit two rows of tags inside the value containers
type OverflowState = {
  isOverflowing: boolean;
  overflowIndex: number;
};

export const ValueContainerWithTruncation = ({
  children,
}: {
  children: ReactNode;
}) => {
  const valueContainerRef = useRef<Maybe<HTMLDivElement>>(null);
  const childCount = Children.count(children);
  const [{ isOverflowing, overflowIndex }, setOverflowState] =
    useState<OverflowState>({
      isOverflowing: false,
      overflowIndex: 0,
    });

  // Always use Children.toArray so the Input element's key is consistent between
  // overflow and non-overflow renders — avoiding unexpected remounts that cause
  // the Input to blur and break click-outside-to-close behavior.
  const reactChildren = Children.toArray(children);
  const InputEl = reactChildren[reactChildren.length - 1];
  // All children except the Input are tags.
  const tagChildren = reactChildren.slice(0, -1);
  // Always show at least 1 tag in overflow mode so the selection is identifiable.
  const visibleTagCount = Math.max(1, overflowIndex - 1);
  const hiddenTagCount = tagChildren.length - visibleTagCount;

  useLayoutEffect(() => {
    const valueContainerEl = valueContainerRef.current;
    const parentRect = valueContainerEl?.getBoundingClientRect();
    if (!parentRect || !valueContainerEl) return;

    const overflowingChildIndex = Array.from(
      valueContainerEl.children
    ).findIndex(
      child =>
        child.getBoundingClientRect().top - parentRect.top >=
        MAX_DISTANCE_IN_PX_FROM_CHILD_TO_PARENT_CONTAINER_TOP
    );

    // Only enter overflow when at least 1 tag would actually be hidden — otherwise
    // the badge represents nothing and the state gets stuck (e.g. 1 long tag that
    // wraps but has no sibling to hide).
    const prospectiveHiddenTagCount =
      childCount - 1 - Math.max(1, overflowingChildIndex - 1);

    if (
      overflowingChildIndex !== -1 &&
      !isOverflowing &&
      prospectiveHiddenTagCount > 0
    ) {
      setOverflowState({
        isOverflowing: true,
        overflowIndex: overflowingChildIndex,
      });
    } else if (isOverflowing && hiddenTagCount <= 0) {
      setOverflowState({ isOverflowing: false, overflowIndex: 0 });
    }
  }, [childCount, hiddenTagCount, isOverflowing]);

  if (!isOverflowing || hiddenTagCount <= 0) {
    return (
      <ValueContainerStyle ref={valueContainerRef}>
        {reactChildren}
      </ValueContainerStyle>
    );
  }

  const VisibleChildren = tagChildren.slice(0, visibleTagCount);

  return (
    <ValueContainerStyle ref={valueContainerRef}>
      {VisibleChildren}
      <Badge
        iconName={IconName.MORE_HORIZ}
        statusType={StatusType.DEFAULT}
        label={`+${hiddenTagCount} more`}
      />
      {InputEl}
    </ValueContainerStyle>
  );
};
